import React, { memo, useMemo, CSSProperties, useRef, useLayoutEffect } from 'react'
import { Moment } from 'moment-timezone'
import { buildDateTime, floor } from '../utils/utils'

export interface ITimeSuggestionProps {
  label: string
  anchorDateTime?: Moment
  targetDate?: string
  selected: boolean
  timeFormat: string
  onClick: (label: string, dayDiff: number) => void
}

export const TimeSuggestion = memo<ITimeSuggestionProps>(
  ({ anchorDateTime, label, onClick, selected, targetDate, timeFormat }) => {
    const dateTime = useMemo(
      () => (targetDate ? buildDateTime(targetDate, label, timeFormat) : undefined),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [label, targetDate]
    )

    const relativeTime = useMemo(() => {
      const minutesDiff =
        anchorDateTime && dateTime ? dateTime.diff(anchorDateTime, 'm') : undefined
      if (minutesDiff === undefined) return ''

      if (Math.abs(minutesDiff) < 60) return `(${minutesDiff} min)`

      const hoursDiff = minutesDiff / 60
      if (Math.abs(hoursDiff) < 24) {
        return hoursDiff % 1 !== 0
          ? `(${floor(hoursDiff)} hr ${Math.abs(minutesDiff) % 60} min)`
          : `(${hoursDiff} hr)`
      }

      return ''
    }, [anchorDateTime, dateTime])

    const clickHandler = () => {
      onClick(label, 0)
    }

    return (
      <li
        className={selected ? 'time-suggestions-selected' : ''}
        role='button'
        tabIndex={-1}
        data-test='time-suggestion'
        onMouseDown={clickHandler}>
        {label}&nbsp;{relativeTime}
      </li>
    )
  }
)

export interface ITimeSuggestionsProps {
  options: string[]
  selectedOption: string
  targetDate?: string
  anchorDateTime?: Moment
  timeFormat: string
  style: CSSProperties
  dataTest?: string
  onOptionSelect: (value: string, dayDiff: number) => void
}

export const TimeSuggestions = memo<ITimeSuggestionsProps>(
  ({
    anchorDateTime,
    dataTest,
    onOptionSelect,
    options,
    selectedOption,
    style,
    targetDate,
    timeFormat,
  }) => {
    const listRef = useRef<HTMLUListElement>(null)

    useLayoutEffect(() => {
      const { current: ul } = listRef
      if (ul) {
        const optionIndex = options.indexOf(selectedOption)
        const item = ul.firstChild
        if (item && optionIndex) {
          const itemHeight = (item as HTMLLIElement).getBoundingClientRect().height
          ul.scrollTop = (optionIndex - 2) * itemHeight
        } else {
          ul.scrollTop = 0
        }
      }
    }, [selectedOption, anchorDateTime])

    return (
      <ul
        className='time-suggestions'
        ref={listRef}
        style={style}
        aria-hidden='true'
        data-test={dataTest}>
        {options.map(option => (
          <TimeSuggestion
            key={option}
            label={option}
            targetDate={targetDate}
            anchorDateTime={anchorDateTime}
            selected={option === selectedOption}
            timeFormat={timeFormat}
            onClick={onOptionSelect}
          />
        ))}
      </ul>
    )
  }
)
